import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import { useFormikContext } from "formik";
import Card from "../Card";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import YesNoRow from "../YesNoRow";
import Input from "../../../../../Components/Common/Input/Input";
import SearchableSelect from "../../../../../Components/Common/SearchableSelect/SearchableSelect";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { autoFillClass, prefillProps } from "../../utils/prefilled";
import { PreconFormValues, YesNoValue } from "../../types/precon.types";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";

interface Props {
  prefilledFields: Set<string>;
}

const Step4ContractDelivery = ({ prefilledFields }: Props) => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext<PreconFormValues>();

  const af = (field: keyof PreconFormValues) =>
    autoFillClass(field, prefilledFields);
  const pf = (field: keyof PreconFormValues) =>
    prefillProps(field, prefilledFields);
  const err = (field: keyof PreconFormValues) =>
    errors[field] as string | undefined;
  const isTouched = (field: keyof PreconFormValues) =>
    touched[field] as boolean | undefined;

  const hasErr = (
    field: keyof PreconFormValues,
    have?: number,
    min?: number,
  ): boolean => {
    const val = String(values[field] ?? "");
    const len = val.length;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && len > 0 && len < min) return true;
    return !!(isTouched(field) && err(field));
  };

  const handleYesNoChange = (id: string, value: YesNoValue) => {
    const updated = values.yesNoItems.map((item) =>
      item.id === id ? { ...item, value } : item,
    );
    setFieldValue("yesNoItems", updated);
  };

  return (
    <div className="screen active">
      <SectionHeader
        title="Contract Delivery Method & Review"
        subtitle="Pre-populated from GC checklist Q&A. Toggle each item and add CSC narrative comments."
      />

      <SourceBanner
        icon="📋"
        title="Pre-filled from GC Q&A responses"
        text="Retainage, bonds, liquidated damages, and other contract terms pulled from the GC's submitted checklist."
      />

      <Card title="Contract Structure" badge="Editable" badgeType="edit">
        <div className="ynRow">
          <div>
            <div className="ynLabel">Contract Type</div>
            <div className="ynSub">Nature of the GC engagement</div>
          </div>
          <div className="contractField">
            <SearchableSelect
              value={values.contractType}
              placeholder="Select type"
              disableSearch
              options={[
                {
                  value: "Owner / GC (No separate contract)",
                  label: "Owner / GC (No separate contract)",
                },
                { value: "Lump Sum Contract", label: "Lump Sum Contract" },
                {
                  value: "Guaranteed Maximum Price (GMP)",
                  label: "Guaranteed Maximum Price (GMP)",
                },
                { value: "Cost Plus Fee", label: "Cost Plus Fee" },
                { value: "Time & Materials", label: "Time & Materials" },
              ]}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              onChange={(val) => setFieldValue("contractType", val)}
              error={hasErr("contractType")}
              className={`selectCompact${af("contractType") === "fi autoFill" ? " autoFill" : ""}`}
            />
          </div>
        </div>
        <div className="ynRow">
          <div>
            <div className="ynLabel">Contractor Fee</div>
            <div className="ynSub">GC overhead and profit</div>
          </div>
          <div className="contractField">
            <Input
              name="contractorFee"
              className={`fi inputCompact${hasErr("contractorFee", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.contractorFee}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
            />
            <ErrorMessage
              value={values.contractorFee}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("contractorFee")}
              error={err("contractorFee")}
            />
          </div>
        </div>
        <div className="ynRow">
          <div>
            <div className="ynLabel">Contingency Amount</div>
            <div className="ynSub">From budget</div>
          </div>
          <div className="contractField">
            <Input
              name="contingencyDisplay"
              className={`inputCompact ${af("contingencyDisplay")}${hasErr("contingencyDisplay", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.contingencyDisplay}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("contingencyDisplay")}
            />
            <ErrorMessage
              value={values.contingencyDisplay}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("contingencyDisplay")}
              error={err("contingencyDisplay")}
            />
          </div>
        </div>
        <div className="ynRow">
          <div>
            <div className="ynLabel">Retainage</div>
            <div className="ynSub">% withheld from subcontractors</div>
          </div>
          <div className="contractField">
            <Input
              name="retainage"
              className={`inputCompact ${af("retainage")}${hasErr("retainage", CHAR_LIMIT.SHORT) ? " error-input-red" : ""}`}
              value={values.retainage}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={CHAR_LIMIT.SHORT}
              {...pf("retainage")}
            />
            <ErrorMessage
              value={values.retainage}
              have={CHAR_LIMIT.SHORT}
              touched={isTouched("retainage")}
              error={err("retainage")}
            />
          </div>
        </div>
      </Card>

      <Card title="Project Requirements — Yes / No / N/A" badge={undefined}>
        {values.yesNoItems.map((item) => (
          <YesNoRow
            key={item.id}
            label={item.label}
            subLabel={item.subLabel}
            value={item.value}
            onChange={(value: YesNoValue) => handleYesNoChange(item.id, value)}
          />
        ))}
      </Card>

      <Card
        title="CSC Narrative — Contract Delivery Comments"
        badge="Editable"
        badgeType="edit"
      >
        <div className="field">
          <label>
            Key Findings & Observations <span className="req">*</span>
          </label>
          <TextArea
            name="contractNarrative"
            className={`ft textareaL${hasErr("contractNarrative", CHAR_LIMIT.DESCRIPTION) ? " error-input-red" : ""}`}
            value={values.contractNarrative}
            onChange={handleChange}
            onBlur={handleBlur}
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.contractNarrative}
            touched={isTouched("contractNarrative")}
            error={err("contractNarrative")}
          />
        </div>
      </Card>
    </div>
  );
};

export default Step4ContractDelivery;
